import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { type PaymentStatus, type PaymentType } from "@/mocks/admin/payments";
import { usePayments } from "@/hooks/admin/useAdminData";

const statusStyles: Record<
  PaymentStatus,
  { label: string; bg: string; text: string; dot: string }
> = {
  succeeded: { label: "Succeeded", bg: "bg-emerald-50", text: "text-emerald-700", dot: "bg-emerald-500" },
  pending: { label: "Pending", bg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-500" },
  failed: { label: "Failed", bg: "bg-rose-50", text: "text-rose-700", dot: "bg-rose-500" },
  refunded: { label: "Refunded", bg: "bg-ink-100", text: "text-ink-700", dot: "bg-ink-500" },
};

const typeStyles: Record<PaymentType, { label: string; color: string }> = {
  deposit: { label: "Deposit", color: "text-brand-700 bg-brand-50" },
  balance: { label: "Balance", color: "text-emerald-700 bg-emerald-50" },
  full: { label: "Full Payment", color: "text-indigo-700 bg-indigo-50" },
  refund: { label: "Refund", color: "text-rose-700 bg-rose-50" },
};

const tabs = [
  { key: "all", label: "All" },
  { key: "succeeded", label: "Succeeded" },
  { key: "pending", label: "Pending" },
  { key: "failed", label: "Failed" },
  { key: "refunded", label: "Refunded" },
];

export default function TransactionsTable() {
  const [active, setActive] = useState("all");
  const [search, setSearch] = useState("");
  const { data: payments, loading } = usePayments();

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: payments.length };
    payments.forEach((p) => {
      c[p.status] = (c[p.status] || 0) + 1;
    });
    return c;
  }, [payments]);

  const filtered = useMemo(() => {
    let list = payments;
    if (active !== "all") list = list.filter((p) => p.status === active);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (p) =>
          p.id.toLowerCase().includes(q) ||
          p.customer.toLowerCase().includes(q) ||
          p.projectId.toLowerCase().includes(q),
      );
    }
    return list;
  }, [active, search, payments]);

  return (
    <div className="bg-white border border-ink-100 rounded-lg">
      {/* Tabs */}
      <div className="flex items-center gap-1 px-3 pt-2 border-b border-ink-100 overflow-x-auto scrollbar-thin">
        {tabs.map((t) => {
          const isActive = t.key === active;
          return (
            <button
              key={t.key}
              onClick={() => setActive(t.key)}
              className={`relative px-3 py-3 text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors ${
                isActive ? "text-ink-900" : "text-ink-500 hover:text-ink-700"
              }`}
            >
              <span className="flex items-center gap-2">
                {t.label}
                <span
                  className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                    isActive ? "bg-ink-900 text-white" : "bg-cream-100 text-ink-600"
                  }`}
                >
                  {counts[t.key] || 0}
                </span>
              </span>
              {isActive && (
                <span className="absolute left-3 right-3 -bottom-px h-0.5 bg-brand-500 rounded-full"></span>
              )}
            </button>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 px-4 py-3 border-b border-ink-100">
        <div className="relative flex-1 min-w-0">
          <span className="w-5 h-5 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-ink-400">
            <i className="ri-search-line text-sm"></i>
          </span>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by transaction ID, customer, or project…"
            className="w-full text-sm pl-10 pr-3 py-2.5 rounded-md bg-cream-50 border border-ink-100 focus:bg-white focus:border-ink-300 outline-none transition-colors"
          />
        </div>
        <button className="inline-flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-semibold border border-ink-200 text-ink-700 hover:bg-cream-100 cursor-pointer whitespace-nowrap">
          <i className="ri-download-line"></i>
          Export CSV
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-cream-50 border-b border-ink-100 text-ink-500">
              <th className="text-left font-semibold uppercase text-[10px] tracking-wider px-5 py-3">
                Transaction
              </th>
              <th className="text-left font-semibold uppercase text-[10px] tracking-wider px-5 py-3">
                Customer / Project
              </th>
              <th className="text-left font-semibold uppercase text-[10px] tracking-wider px-5 py-3">
                Method
              </th>
              <th className="text-left font-semibold uppercase text-[10px] tracking-wider px-5 py-3">
                Type
              </th>
              <th className="text-left font-semibold uppercase text-[10px] tracking-wider px-5 py-3">
                Status
              </th>
              <th className="text-right font-semibold uppercase text-[10px] tracking-wider px-5 py-3">
                Amount
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} className="px-5 py-10 text-center text-sm text-ink-500">
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  Loading transactions…
                </td>
              </tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="px-5 py-10 text-center text-sm text-ink-500">
                  No transactions found.
                </td>
              </tr>
            )}
            {!loading && filtered.map((p) => {
              const s = statusStyles[p.status];
              const t = typeStyles[p.type];
              return (
                <tr
                  key={p.id}
                  className="border-b border-ink-100 last:border-b-0 hover:bg-cream-50 transition-colors"
                >
                  <td className="px-5 py-3.5">
                    <div className="font-mono text-[11px] font-bold text-ink-700">{p.id}</div>
                    <div className="text-[10px] text-ink-400">{p.date}</div>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2.5">
                      <div
                        className={`w-8 h-8 rounded-full ${p.color} text-white text-[10px] font-bold flex items-center justify-center shrink-0`}
                      >
                        {p.initials}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-ink-900 truncate">
                          {p.customer}
                        </div>
                        <Link
                          to={`/projects/${p.projectId}`}
                          className="font-mono text-[10px] font-bold text-brand-700 hover:underline cursor-pointer"
                        >
                          {p.projectId}
                        </Link>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2">
                      <span className="w-7 h-7 flex items-center justify-center rounded-md bg-cream-100 text-ink-700">
                        <i className={`${p.methodIcon} text-base`}></i>
                      </span>
                      <div>
                        <div className="text-sm font-semibold text-ink-900">{p.method}</div>
                        {p.last4 !== "—" && (
                          <div className="text-[10px] text-ink-400 font-mono">•••• {p.last4}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${t.color}`}
                    >
                      {t.label}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span
                      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold ${s.bg} ${s.text}`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`}></span>
                      {s.label}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <div
                      className={`text-sm font-bold tabular-nums ${
                        p.type === "refund" ? "text-rose-600" : "text-ink-900"
                      }`}
                    >
                      {p.type === "refund" ? "-" : ""}${p.amount.toLocaleString()}
                    </div>
                    <div className="text-[10px] text-ink-400">
                      fee ${Math.abs(p.fee).toFixed(2)}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-3 border-t border-ink-100 bg-cream-50">
        <div className="text-xs text-ink-500">
          Showing <span className="font-semibold text-ink-900">{filtered.length}</span> of{" "}
          <span className="font-semibold text-ink-900">{payments.length}</span> transactions
        </div>
        <div className="flex items-center gap-1">
          <button className="w-8 h-8 flex items-center justify-center rounded-md text-ink-400 hover:bg-cream-100 cursor-pointer">
            <i className="ri-arrow-left-s-line"></i>
          </button>
          <button className="w-8 h-8 flex items-center justify-center rounded-md text-sm font-semibold bg-ink-900 text-white cursor-pointer">
            1
          </button>
          <button className="w-8 h-8 flex items-center justify-center rounded-md text-sm font-semibold text-ink-500 hover:bg-cream-100 cursor-pointer">
            2
          </button>
          <button className="w-8 h-8 flex items-center justify-center rounded-md text-ink-500 hover:bg-cream-100 cursor-pointer">
            <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>
      </div>
    </div>
  );
}